'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Menu, X, ChevronDown, LogOut, User, Shield, Calendar, Settings, Trophy, Gauge, MessageSquare, ShoppingBag, Search, MapPin, MoreHorizontal, Bell } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/context/AuthContext';
import { createClient } from '@/lib/supabase/client';

const PRIMARY_NAV = [
  { href: '/search', label: 'Search', icon: Search },
  { href: '/events', label: 'Events', icon: Calendar },
  { href: '/live-results', label: 'Results', icon: Trophy },
  { href: '/tracks', label: 'Tracks', icon: MapPin },
];

const SECONDARY_NAV = [
  { href: '/lounge', label: 'Lounge', icon: MessageSquare },
  { href: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
  { href: '/leaderboard', label: 'Standings', icon: Trophy },
  { href: '/about', label: 'About Us', icon: Shield },
];

const ALL_NAV = [...PRIMARY_NAV, ...SECONDARY_NAV];

const NOTIFICATION_SOUNDS: Record<string, string> = {
  chime: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
  ding: 'https://assets.mixkit.co/active_storage/sfx/2868/2868-preview.mp3',
  pop: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3',
  bell: 'https://assets.mixkit.co/active_storage/sfx/2020/2020-preview.mp3',
  alert: 'https://assets.mixkit.co/active_storage/sfx/1862/1862-preview.mp3',
};

function playNotificationSound(soundKey: string) {
  try {
    const url = NOTIFICATION_SOUNDS[soundKey] || NOTIFICATION_SOUNDS.chime;
    const audio = new Audio(url);
    audio.volume = 0.5;
    audio.play().catch(() => null);
  } catch { /* non-blocking */ }
}

export default function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut, isAdmin, isOrganizer, isTrackManager } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [eventsNotifCount, setEventsNotifCount] = useState(0);
  const [adminNotifCount, setAdminNotifCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifSound, setNotifSound] = useState('chime');
  const supabase = createClient();
  const moreRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const prevNotifCount = useRef(0);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const isSecondaryActive = SECONDARY_NAV.some(l => isActive(l.href));

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    setMobileOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreMenuOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load notification sound preference
  useEffect(() => {
    const fetchSound = async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'notification_sound').maybeSingle();
      if (data?.value) setNotifSound(data.value);
    };
    fetchSound();
  }, []);

  // Fetch user notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) { setNotifications([]); return; }
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    if (data) {
      const unread = data.filter((n: any) => !n.is_read).length;
      if (unread > prevNotifCount.current && prevNotifCount.current >= 0) {
        playNotificationSound(notifSound);
      }
      prevNotifCount.current = unread;
      setNotifications(data);
    }
  }, [user, notifSound]);

  useEffect(() => {
    if (!user) {
      setEventsNotifCount(0);
      setAdminNotifCount(0);
      setNotifications([]);
      return;
    }

    const fetchNotifs = async () => {
      if (isAdmin) {
        const { count } = await supabase
          .from('event_registrations')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending');
        setAdminNotifCount(count ?? 0);
      } else {
        const { count } = await supabase
          .from('event_registrations')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'approved');
        setEventsNotifCount(count ?? 0);
      }
    };

    fetchNotifs();
    fetchNotifications();

    const channel = supabase
      .channel('navbar-notifs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_registrations' }, () => {
        fetchNotifs();
      })
      .subscribe();

    // Real-time notifications
    const notifChannel = supabase
      .channel(`user-notifications-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(notifChannel);
    };
  }, [user, isAdmin, fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markAsRead = async (notifId: string, link?: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', notifId);
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
    setNotifOpen(false);
    // Use router.push for internal links to avoid a full page reload
    if (link) {
      if (link.startsWith('http') || link.startsWith('//')) {
        window.location.href = link;
      } else {
        router.push(link);
      }
    }
  };

  const markAllRead = async () => {
    if (!user) return;
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-card/95 backdrop-blur-md">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 gap-2">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo size={220} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-0.5 flex-1 justify-center overflow-visible min-w-0">
            {PRIMARY_NAV.map((link) => (
              <Link
                key={`nav-${link.href}`}
                href={link.href}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 whitespace-nowrap ${
                  isActive(link.href)
                    ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <link.icon size={14} />
                {link.label}
                {link.href === '/live-results' && (
                  <span className="flex items-center gap-1 ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold badge-live">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary live-pulse inline-block" />
                    LIVE
                  </span>
                )}
                {link.href === '/events' && !isAdmin && eventsNotifCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
                    {eventsNotifCount}
                  </span>
                )}
              </Link>
            ))}

            {/* More dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 ${
                  isSecondaryActive ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <MoreHorizontal size={14} />
                More
                <ChevronDown size={12} className={`transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreMenuOpen && (
                <div className="absolute left-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
                  {SECONDARY_NAV.map(link => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMoreMenuOpen(false)}
                      className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold transition-colors ${
                        isActive(link.href) ? 'text-primary bg-primary/10' : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <link.icon size={15} className="text-muted-foreground" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {isAdmin && (
              <Link href="/admin" className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 ${isActive('/admin') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <Shield size={14} />Admin
                {adminNotifCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
                    {adminNotifCount}
                  </span>
                )}
              </Link>
            )}
            {isOrganizer && (
              <Link href="/organizer" className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 ${isActive('/organizer') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <Settings size={14} />Panel
              </Link>
            )}
            {isTrackManager && (
              <Link href="/track-manager" className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 ${isActive('/track-manager') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <MapPin size={14} />My Track
              </Link>
            )}
            {(isAdmin || isOrganizer) && (
              <Link href="/race-control" className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-md transition-all duration-150 ${isActive('/race-control') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <Gauge size={14} />Race
              </Link>
            )}
          </div>

          {/* Auth Area */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {/* Notification Bell */}
            {user && (
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <p className="text-sm font-bold text-foreground">Notifications</p>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline">Mark all read</button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell size={24} className="text-muted-foreground/30 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <button
                            key={n.id}
                            onClick={() => markAsRead(n.id, n.link)}
                            className={`w-full text-left px-4 py-3 border-b border-border/50 last:border-0 hover:bg-muted/40 transition-colors ${!n.is_read ? 'bg-primary/5' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                              <div className={!n.is_read ? '' : 'ml-4'}>
                                <p className="text-xs font-bold text-foreground">{n.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                                <p className="text-[10px] text-muted-foreground/60 mt-1">{new Date(n.created_at).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted/50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center overflow-hidden">
                    <User size={14} className="text-primary" />
                  </div>
                  <span className="text-xs font-bold text-foreground max-w-[100px] truncate">
                    {(user as any).race_name || user.full_name || user.email?.split('@')[0]}
                  </span>
                  <ChevronDown size={12} className={`text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground">{user.full_name || 'Driver'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <Link href="/my-events" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                        <Calendar size={15} className="text-muted-foreground" />My Events
                      </Link>
                      <Link href="/profile" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                        <User size={15} className="text-muted-foreground" />My Profile
                      </Link>
                      {isAdmin && (
                        <Link href="/admin" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <Shield size={15} className="text-muted-foreground" />Admin Panel
                        </Link>
                      )}
                      {isOrganizer && (
                        <Link href="/organizer" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <Settings size={15} className="text-muted-foreground" />Organizer Panel
                        </Link>
                      )}
                      {isTrackManager && (
                        <Link href="/track-manager" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <MapPin size={15} className="text-muted-foreground" />My Track
                        </Link>
                      )}
                      <button onClick={handleSignOut} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-negative hover:bg-negative/10 transition-colors">
                        <LogOut size={15} />Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link href="/login" className="px-3 py-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">Sign In</Link>
                <Link href="/register" className="px-3 py-2 text-sm font-semibold rounded-md btn-primary">Register</Link>
              </div>
            )}
          </div>

          {/* Mobile: Bell + Hamburger */}
          <div className="md:hidden flex items-center gap-1">
            {user && (
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <p className="text-sm font-bold text-foreground">Notifications</p>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline">Mark all read</button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-6 text-center">
                          <p className="text-xs text-muted-foreground">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <button
                            key={n.id}
                            onClick={() => markAsRead(n.id, n.link)}
                            className={`w-full text-left px-4 py-3 border-b border-border/50 last:border-0 hover:bg-muted/40 transition-colors ${!n.is_read ? 'bg-primary/5' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                              <div className={!n.is_read ? '' : 'ml-4'}>
                                <p className="text-xs font-bold text-foreground">{n.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <button className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card animate-slide-down">
          <div className="overflow-y-auto max-h-[calc(100vh-64px)]">
          <div className="px-4 py-3 space-y-1">
            {ALL_NAV.map((link) => (
              <Link key={`mobile-nav-${link.href}`} href={link.href} className={`relative flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors ${isActive(link.href) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`} onClick={() => setMobileOpen(false)}>
                <link.icon size={16} />{link.label}
                {link.href === '/live-results' && (
                  <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold badge-live">LIVE</span>
                )}
                {link.href === '/events' && !isAdmin && eventsNotifCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1">
                    {eventsNotifCount}
                  </span>
                )}
              </Link>
            ))}
            {isAdmin && (
              <Link href="/admin" className="relative flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>
                <Shield size={16} />Admin
                {adminNotifCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1">
                    {adminNotifCount}
                  </span>
                )}
              </Link>
            )}
            {isOrganizer && (
              <Link href="/organizer" className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>
                <Settings size={16} />Organizer Panel
              </Link>
            )}
            {isTrackManager && (
              <Link href="/track-manager" className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>
                <MapPin size={16} />My Track
              </Link>
            )}
            {(isAdmin || isOrganizer) && (
              <Link href="/race-control" className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors ${isActive('/race-control') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`} onClick={() => setMobileOpen(false)}>
                <Gauge size={16} />Race Control
              </Link>
            )}
          </div>
          <div className="px-4 py-3 border-t border-border">
            {user ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{user.full_name || 'Driver'}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <Link href="/my-events" className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>
                  <Calendar size={16} />My Events
                </Link>
                <Link href="/profile" className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>
                  <User size={16} />My Profile
                </Link>
                <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-negative hover:bg-negative/10 transition-colors">
                  <LogOut size={16} />Sign Out
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link href="/login" className="w-full text-center px-4 py-3 text-sm font-bold uppercase tracking-wide rounded-md border border-border text-foreground hover:bg-muted/50 transition-colors" onClick={() => setMobileOpen(false)}>Sign In</Link>
                <Link href="/register" className="w-full text-center px-4 py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary" onClick={() => setMobileOpen(false)}>Register</Link>
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </nav>
  );
}